import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, Loader2 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import QuizReviewMode from './quiz/QuizReviewMode';
import PracticeModeView from './quiz/components/PracticeModeView';
import PackagePurchaseModal from '../components/PackagePurchaseModal';
import { checkExamPermission } from '../utils/permissionCheck';
import { mapDbQuestionsToStandard } from '../utils/questionMapper';
import type { Question, Paper } from './quiz/types';

export default function WrongQuestionsReviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgCode = searchParams.get('org') || '';
  const catCode = searchParams.get('cat') || '';
  const levelText = searchParams.get('level') || '';

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState<Record<string, string>>({});

  // 练习模式状态
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [practiceAnswers, setPracticeAnswers] = useState<Record<string, string>>({});
  const [revealedQuestions, setRevealedQuestions] = useState<Set<string>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);

  // 权限检测状态
  const [checkingPermission, setCheckingPermission] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPaperForPurchase, setSelectedPaperForPurchase] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [orgCode, catCode, levelText]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }
      setCurrentUser(session.user);

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action: 'get_wrong_questions',
          organization_code: orgCode,
          category_code: catCode,
          level_text: levelText
        })
      });

      const result = await response.json();
      if (result.success) {
        setTotalCount(result.total_count || 0);
        setWrongAnswers(result.user_answers || {});

        const virtualPaper: Paper = {
          id: result.paper?.id || 'virtual-wrong',
          title: `${(result.paper?.title || '错题回顾').split(' - 错题回顾')[0]} - 错题回顾`,
          total_score: 0,
          category_type: result.paper?.category_type || catCode,
          level_text: result.paper?.level_text || '',
          organization: result.paper?.organization || orgCode
        };
        setPaper(virtualPaper);

        const mappedQuestions: Question[] = mapDbQuestionsToStandard(result.questions || []);
        setQuestions(mappedQuestions);
      } else {
        setQuestions([]);
        setPaper(null);
      }
    } catch (error) {
      setQuestions([]);
      setPaper(null);
    }
    setLoading(false);
  };

  // 进入练习模式
  const enterPracticeMode = () => {
    setIsPracticeMode(true);
    setPracticeAnswers({});
    setRevealedQuestions(new Set());
    setCurrentIndex(0);
  };

  // 退出练习模式
  const exitPracticeMode = () => {
    setIsPracticeMode(false);
    setPracticeAnswers({});
    setRevealedQuestions(new Set());
  };

  // 检查权限后进入答题模式
  const handleCheckPermission = async () => {
    if (!currentUser?.id || !paper) return;

    setCheckingPermission(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }

      // 检查刷题包权限
      const permResult = await checkExamPermission(
        session.user.id,
        paper.organization || '',
        paper.category_type || '',
        paper.level_text || ''
      );

      if (permResult.hasPermission) {
        // 有权限，直接进入答题模式
        enterPracticeMode();
      } else {
        // 无权限，弹出购买弹窗
        setSelectedPaperForPurchase({
          id: paper.id,
          title: paper.title,
          organization: paper.organization || '',
          category_type: paper.category_type || '',
          level_text: paper.level_text || '',
        });
        setShowPurchaseModal(true);
      }
    } catch {
      // 权限检查失败，降级处理：直接进入答题模式
      enterPracticeMode();
    } finally {
      setCheckingPermission(false);
    }
  };

  // 练习模式：选择答案后立即揭示
  const handlePracticeAnswerChange = (questionId: string, answer: string) => {
    if (answer) {
      setPracticeAnswers(prev => ({ ...prev, [questionId]: answer }));
      const question = questions.find(q => q.id === questionId);
      if (question && (question.type === 'single_choice' || question.type === 'multi_choice' || question.type === 'judge')) {
        setRevealedQuestions(prev => new Set([...prev, questionId]));
      }
    } else {
      setPracticeAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[questionId];
        return newAnswers;
      });
    }
  };

  const handlePracticeTextAnswerChange = (questionId: string, text: string) => {
    if (text.trim()) {
      setPracticeAnswers(prev => ({ ...prev, [questionId]: text, [`${questionId}_text`]: text }));
    } else {
      setPracticeAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[questionId];
        delete newAnswers[`${questionId}_text`];
        return newAnswers;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
          <p className="text-gray-500">加载错题...</p>
        </div>
      </div>
    );
  }

  if (!paper || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center max-w-md">
          <XCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">未找到错题</h3>
          <p className="text-gray-500 mb-4">该分类下暂无错题</p>
          <button
            onClick={() => navigate('/wrong-questions')}
            className="px-6 py-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all font-medium"
          >
            返回错题首页
          </button>
        </div>
      </div>
    );
  }

  // 作答详情模式：使用 QuizReviewMode
  if (!isPracticeMode) {
    return (
      <>
        <QuizReviewMode
          paper={paper}
          examQuestions={questions}
          answers={wrongAnswers}
          score={0}
          timeLeft={0}
          objectiveScore={null}
          objectiveTotalScore={null}
          programmingScore={null}
          questionFeedbacks={null}
          currentUser={currentUser}
          paperId={paper.id}
          hideScoreAndTime={true}
          isWrongQuestionMode={true}
          showAnswerModeButton={true}
          isInAnswerMode={false}
          onBack={() => navigate('/wrong-questions')}
          onCheckPermission={handleCheckPermission}
          onExitAnswerMode={exitPracticeMode}
          checkingPermission={checkingPermission}
        />
        {showPurchaseModal && selectedPaperForPurchase && (
          <PackagePurchaseModal
            paper={selectedPaperForPurchase}
            onClose={() => {
              setShowPurchaseModal(false);
              setSelectedPaperForPurchase(null);
            }}
          />
        )}
      </>
    );
  }

  // 练习模式：使用通用 PracticeModeView 组件
  return (
    <PracticeModeView
      questions={questions}
      paper={paper}
      currentUser={currentUser}
      practiceAnswers={practiceAnswers}
      revealedQuestions={revealedQuestions}
      currentIndex={currentIndex}
      showSidebar={showSidebar}
      onExitPracticeMode={exitPracticeMode}
      onBack={() => navigate('/wrong-questions')}
      isWrongQuestionMode={true}
      onAnswerChange={handlePracticeAnswerChange}
      onTextAnswerChange={handlePracticeTextAnswerChange}
      onSetCurrentIndex={setCurrentIndex}
      onSetShowSidebar={setShowSidebar}
      onSetRevealedQuestions={setRevealedQuestions}
      theme="rose"
      titleFallback="错题练习"
    />
  );
}